import { Link } from "@tanstack/react-router";
import { Package, TriangleAlertIcon } from "@/components/icons";
import { Badge, Skeleton } from "@/components/kit";
import { cn } from "@/lib/utils";
import { formatQtyWithUnit } from "@/modules/products/domain/unit-conversion";
import { formatLak } from "@/shared/lib/currency";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { useVariantStockLotsQuery } from "../api/queries";

type VariantStockLotsPanelProps = {
  variantId: string;
  stockQuantity: number;
  stockUnitLabel: string;
  /** แสดงเฉพาะรายการ lot — หัว variant อยู่ที่ parent */
  embedded?: boolean;
  variantName?: string;
  withoutLotQuantity?: number;
  compact?: boolean;
};

function LotCoverageBar({
  lotTotal,
  stockQuantity,
  stockUnitLabel,
  unlot,
}: {
  lotTotal: number;
  stockQuantity: number;
  stockUnitLabel: string;
  unlot: number;
}) {
  const coveragePct =
    stockQuantity > 0
      ? Math.min(100, Math.round((lotTotal / stockQuantity) * 100))
      : 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="text-muted-foreground">
          ຢູ່ໃນ lot{" "}
          <span className="font-semibold text-foreground tabular-nums">
            {formatQtyWithUnit(lotTotal, stockUnitLabel)}
          </span>
          {stockQuantity > 0 ? (
            <span className="text-muted-foreground"> · {coveragePct}%</span>
          ) : null}
        </span>
        {unlot > 0 ? (
          <Badge
            variant="outline"
            className="border-destructive/30 bg-destructive/10 text-[10px] text-destructive"
          >
            <TriangleAlertIcon className="size-3" />
            {formatQtyWithUnit(unlot, stockUnitLabel)} ບໍ່ມີ lot
          </Badge>
        ) : (
          <span className="font-medium text-leaf text-xs">lot ຄົບ</span>
        )}
      </div>
      <div
        className="h-2 overflow-hidden rounded-full bg-muted/60"
        role="presentation"
      >
        <div
          className={cn(
            "h-full rounded-full transition-[width]",
            unlot > 0 ? "bg-secondary" : "bg-leaf",
          )}
          style={{ width: `${coveragePct}%` }}
        />
      </div>
    </div>
  );
}

export function VariantStockLotsPanel({
  variantId,
  variantName,
  stockQuantity,
  stockUnitLabel,
  withoutLotQuantity,
  compact,
  embedded = false,
}: VariantStockLotsPanelProps) {
  const lots = useVariantStockLotsQuery(variantId);
  const rows = lots.data?.data ?? [];
  const lotTotal = rows.reduce((sum, row) => sum + row.quantityRemaining, 0);
  const unlot = withoutLotQuantity ?? Math.max(0, stockQuantity - lotTotal);

  if (lots.isLoading) {
    return (
      <Skeleton className={cn("rounded-xl", embedded ? "h-20" : "h-24")} />
    );
  }

  if (embedded) {
    return (
      <div className="flex flex-col gap-3">
        {stockQuantity > 0 ? (
          <LotCoverageBar
            lotTotal={lotTotal}
            stockQuantity={stockQuantity}
            stockUnitLabel={stockUnitLabel}
            unlot={unlot}
          />
        ) : null}

        {rows.length === 0 ? (
          <p className="rounded-xl border border-secondary/25 border-dashed bg-background/50 px-3 py-4 text-center text-muted-foreground text-xs">
            ຍັງບໍ່ມີ lot —{" "}
            <Link
              to="/app/inventory/receive"
              className="font-medium text-secondary underline-offset-2 hover:underline"
            >
              ຮັບເຂົ້າຜ່ານໃບຮັບ
            </Link>
          </p>
        ) : (
          <>
            <p className="font-medium text-[11px] text-muted-foreground uppercase tracking-wide">
              Lot (FIFO — ເກົ່າກ່ອນ)
            </p>
            <ul className={cn("flex flex-col gap-2", compact ? "" : "")}>
              {rows.map((lot, index) => {
                const fillPct =
                  lot.quantityReceived > 0
                    ? Math.round(
                        (lot.quantityRemaining / lot.quantityReceived) * 100,
                      )
                    : 0;
                return (
                  <li
                    key={lot.id}
                    className="rounded-xl border border-border/50 bg-background/70 p-3"
                  >
                    <div className="flex flex-wrap items-start gap-3">
                      <span
                        className="flex size-7 shrink-0 items-center justify-center rounded-full bg-secondary/15 font-bold text-[11px] text-secondary tabular-nums"
                        aria-hidden
                      >
                        {index + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <Package className="size-3.5 shrink-0 text-muted-foreground" />
                          {lot.receiptId && lot.receiptNumber ? (
                            <Link
                              to="/app/inventory/receive/$id"
                              params={{ id: lot.receiptId }}
                              className="font-medium text-secondary text-sm underline-offset-2 hover:underline"
                            >
                              {lot.receiptNumber}
                            </Link>
                          ) : (
                            <span className="font-medium text-sm">
                              ປະກອບຊຸດ
                            </span>
                          )}
                          <span className="text-muted-foreground text-xs">
                            {formatDateTimeLocal(lot.receivedAt)}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                          <span className="font-semibold text-foreground tabular-nums">
                            {formatQtyWithUnit(
                              lot.quantityRemaining,
                              stockUnitLabel,
                            )}
                            {lot.quantityRemaining !== lot.quantityReceived ? (
                              <span className="font-normal text-muted-foreground">
                                {" "}
                                / {lot.quantityReceived}
                              </span>
                            ) : null}
                          </span>
                          <span className="text-muted-foreground tabular-nums">
                            {formatLak(lot.unitLandedCost)}/{stockUnitLabel}
                          </span>
                        </div>
                        <div
                          className="mt-2 h-1 overflow-hidden rounded-full bg-muted/50"
                          role="presentation"
                        >
                          <div
                            className="h-full rounded-full bg-leaf/70"
                            style={{ width: `${fillPct}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-secondary/15 bg-background/40 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          {variantName ? (
            <p className="font-medium text-sm">{variantName}</p>
          ) : null}
          <p className="text-muted-foreground text-xs">
            ສະຕັອກລວມ{" "}
            <span className="font-semibold text-foreground tabular-nums">
              {formatQtyWithUnit(stockQuantity, stockUnitLabel)}
            </span>
          </p>
        </div>
        {unlot > 0 ? (
          <Badge
            variant="outline"
            className="border-destructive/30 bg-destructive/10 text-destructive"
          >
            <TriangleAlertIcon className="size-3.5" />
            {formatQtyWithUnit(unlot, stockUnitLabel)} ບໍ່ມີ lot
          </Badge>
        ) : null}
      </div>

      {stockQuantity > 0 ? (
        <LotCoverageBar
          lotTotal={lotTotal}
          stockQuantity={stockQuantity}
          stockUnitLabel={stockUnitLabel}
          unlot={unlot}
        />
      ) : null}

      {rows.length === 0 ? (
        <p className="text-muted-foreground text-xs">
          ຍັງບໍ່ມີ lot —{" "}
          <Link
            to="/app/inventory/receive"
            className="text-secondary underline-offset-2 hover:underline"
          >
            ຮັບເຂົ້າຜ່ານໃບຮັບ
          </Link>
        </p>
      ) : (
        <ul className={`flex flex-col gap-1.5 ${compact ? "" : "mt-1"}`}>
          {rows.map((lot) => (
            <li
              key={lot.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/60 bg-muted/20 px-2.5 py-2 text-xs"
            >
              <div className="flex min-w-0 items-center gap-2">
                <Package className="size-3.5 shrink-0 text-muted-foreground" />
                {lot.receiptId && lot.receiptNumber ? (
                  <Link
                    to="/app/inventory/receive/$id"
                    params={{ id: lot.receiptId }}
                    className="truncate font-medium text-secondary underline-offset-2 hover:underline"
                  >
                    {lot.receiptNumber}
                  </Link>
                ) : (
                  <span className="truncate font-medium">ປະກອບຊຸດ</span>
                )}
                <span className="text-muted-foreground">
                  {formatDateTimeLocal(lot.receivedAt)}
                </span>
              </div>
              <div className="flex items-center gap-3 tabular-nums">
                <span>
                  {formatQtyWithUnit(lot.quantityRemaining, stockUnitLabel)}
                  {lot.quantityRemaining !== lot.quantityReceived
                    ? ` / ${lot.quantityReceived}`
                    : ""}
                </span>
                <span className="text-muted-foreground">
                  {formatLak(lot.unitLandedCost)}/{stockUnitLabel}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
